import React from 'react'
import PageHeader from '../../componentsII/PageHeader';
import api from '../../axios/api';
import { TableContainer, TableBody, TableCell, TableFooter, TableHead, TablePagination, TableRow, Table, Paper, Switch, IconButton, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import { useEffect, useState } from 'react';
import { X, Trash2 as DeleteIcon, BadgeCheck, BadgeAlert, PenBoxIcon, PlusIcon } from 'lucide-react';
import { format } from "date-fns";
import CustomButton from '../../componentsII/Buttons';
import { useDispatch } from 'react-redux';
import { showNotification } from '../../redux/slices/notificationSlice';
import AddUpdateEquipmentTypeModal from '../../components/Masters/EquipmentTypeMaster/AddUpdateEquipmentTypeModal';
import { TextField, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import debounce from 'lodash/debounce';
import { Search } from 'lucide-react';

const EquipmentTypesMaster = () => {
  const [isAddUpdateEquipmentTypeModalOpen, setIsAddUpdateEquipmentTypeModalOpen] = useState(false);
  const [selectedEquipmentType, setSelectedEquipmentType] = useState(null);
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [equipmentTypeToDelete, setEquipmentTypeToDelete] = useState(null);

  // Pagination and filter states
  const [totalEquipmentTypes, setTotalEquipmentTypes] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    search: '',
    status: 'ALL',
    usesFuel: 'ALL',
    usesRefrigerant: 'ALL',
    usesIndustrialGas: 'ALL',
    sortBy: 'created_date',
    sortOrder: 'desc'
  });

  const dispatch = useDispatch();

  const getAllEquipmentTypes = async (pageNumber = page, limit = rowsPerPage, filterParams = filters) => {
    try {
      const response = await api.get('/admin/getEquipmentTypes', {
        params: {
          page: pageNumber + 1,
          limit: limit,
          search: filterParams.search,
          isActive: filterParams.status === 'ALL' ? '' : filterParams.status === 'ACTIVE',
          usesFuel: filterParams.usesFuel === 'ALL' ? '' : filterParams.usesFuel === 'YES',
          usesRefrigerant: filterParams.usesRefrigerant === 'ALL' ? '' : filterParams.usesRefrigerant === 'YES',
          usesIndustrialGas: filterParams.usesIndustrialGas === 'ALL' ? '' : filterParams.usesIndustrialGas === 'YES',
          sortBy: filterParams.sortBy,
          sortOrder: filterParams.sortOrder,
          typeName: filterParams.search
        }
      });
      setEquipmentTypes(response.data.data);
      setTotalEquipmentTypes(response.data.pagination.total);
    } catch (error) {
      dispatch(showNotification({
        message: 'Failed to fetch Equipment Types',
        type: 'error',
      }));
    }
  };

  useEffect(() => {
    getAllEquipmentTypes();
  }, [page, rowsPerPage, filters]);

  const debouncedSearch = debounce((value) => { 
    setPage(0);
    setFilters(prev => ({ ...prev, search: value })); setPage(0); 
  }, 500);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRemove = async (id) => {
    const equipmentType = equipmentTypes.find(type => type.id === id);
    setEquipmentTypeToDelete(equipmentType);
    setDeleteDialogOpen(true);
  }

  const confirmDelete = async () => {
    try {
      setIsSubmitting(true);
      const response = await api.delete(`/admin/deleteEquipmentType/${equipmentTypeToDelete.id}`);
      if (response.data.status == 200) {
        dispatch(showNotification({
          message: 'Equipment Type deleted successfully',
          type: "success"
        }));
        getAllEquipmentTypes();
      } else {
        dispatch(showNotification({
          message: 'Failed to delete equipment type',
          type: "error"
        }));
      }
    } catch (error) {
      console.error('Error deleting equipment type:', error);
      dispatch(showNotification({
        message: 'An error occurred while deleting',
        type: "error"
      }));
    } finally {
      setIsSubmitting(false);
      setDeleteDialogOpen(false);
      setEquipmentTypeToDelete(null);
    }
  }

  const handleActivateDeactivate = async (user) => {
    try {
      setIsSubmitting(true);
      const response = await api.post(`/admin/activateDeactivateEquipmentType/${user.id}`, {
        is_active: !user.is_active
      });
      if (response.data.status == 200) {
        dispatch(showNotification({
          message: user.is_active ? 'Equipment Type deactivated successfully' : 'Equipment Type activated successfully',
          type: "success"
        }));
        getAllEquipmentTypes();
      } else {
        dispatch(showNotification({
          message: user.is_active ? 'Failed to deactivate equipment type' : 'Failed to activate equipment type',
          type: "error"
        }));
      }
    } catch (error) {
      console.error('Error activating/deactivating equipment type:', error);
      dispatch(showNotification({
        message: 'An error occurred',
        type: "error"
      }));
    } finally {
      setIsSubmitting(false);
    }
  }

  const handleEdit = async (user) => {
    setSelectedEquipmentType(user);
    setIsAddUpdateEquipmentTypeModalOpen(true);
  }

  // Filter options
  const statusOptions = [
    { value: 'ALL', label: 'All' },
    { value: 'ACTIVE', label: 'Active' },
    { value: 'INACTIVE', label: 'Inactive' }
  ];

  const booleanFilterOptions = [
    { value: 'ALL', label: 'All' },
    { value: 'YES', label: 'Yes' },
    { value: 'NO', label: 'No' }
  ];

  const sortOptions = [
    { value: 'created_date', label: 'Created Date' },
    { value: 'type_name', label: 'Type Name' }
  ];


  return (
    <>
      <PageHeader title="Equipment Types">
        <div className="flex justify-end gap-5 items-center mt-5">
        <div className="flex items-center gap-4 flex-1 flex-wrap">
            {/* Search Field */}
            <TextField
              size="small"
              placeholder="Search by type name..."
              InputProps={{
                startAdornment: <Search className="w-5 h-5 text-gray-500 mr-2" />,
              }}
              onChange={(e) => debouncedSearch(e.target.value)}
              className="min-w-[200px]"
            />

            {/* Status Filter */}
            <FormControl size="small" sx={{ minWidth: 80 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                label="Status"
                onChange={(e) => {
                  setPage(0);
                  setFilters(prev => ({ ...prev, status: e.target.value, page: 0 }))
                }}
              >
                {statusOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Uses Fuel Filter */}
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>Uses Fuel</InputLabel>
              <Select
                value={filters.usesFuel}
                label="Uses Fuel"
                onChange={(e) => {
                  setPage(0);
                  setFilters(prev => ({ ...prev, usesFuel: e.target.value, page: 0 }))
                }}
              >
                {booleanFilterOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Uses Refrigerant Filter */}
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Uses Refrigerant</InputLabel>
              <Select
                value={filters.usesRefrigerant}
                label="Uses Refrigerant"
                onChange={(e) => {
                  setPage(0);
                  setFilters(prev => ({ ...prev, usesRefrigerant: e.target.value, page: 0 }))
                }}
              >
                {booleanFilterOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Uses Industrial Gas Filter */}
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Uses Industrial Gas</InputLabel>
              <Select
                value={filters.usesIndustrialGas}
                label="Uses Industrial Gas"
                onChange={(e) => {
                  setPage(0);
                  setFilters(prev => ({ ...prev, usesIndustrialGas: e.target.value, page: 0 }))
                }}
              >
                {booleanFilterOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Sort By */}
            <FormControl size="small" className="min-w-[150px]">
              <InputLabel>Sort By</InputLabel>
              <Select
                value={filters.sortBy}
                label="Sort By"
                onChange={(e) => {
                  setPage(0);
                  setFilters(prev => ({ ...prev, sortBy: e.target.value }))
                }}
              >
                {sortOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Sort Order */}
            <FormControl size="small" className="min-w-[150px]">
              <InputLabel>Order</InputLabel>
              <Select
                value={filters.sortOrder}
                label="Order"
                onChange={(e) => {
                  setPage(0);
                  setFilters(prev => ({ ...prev, sortOrder: e.target.value }))
                }}
              >
                <MenuItem value="asc">Ascending</MenuItem>
                <MenuItem value="desc">Descending</MenuItem>
              </Select>
            </FormControl>
          </div>
          <CustomButton
            className="bg-green-600 hover:bg-green-700 text-white"
            text={<><PlusIcon /> Add Equipment Type</>}
            click={() => setIsAddUpdateEquipmentTypeModalOpen(true)}
          />
        </div>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Type Name</TableCell>
                <TableCell>Uses Fuel</TableCell>
                <TableCell>Uses Refrigerant</TableCell>
                <TableCell>Uses Industrial Gas</TableCell>
                <TableCell>Is Active</TableCell>
                <TableCell>Created Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {equipmentTypes?.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>{user.type_name}</TableCell>
                  <TableCell title={user.use_fuel ? "uses fuel" : "does not use fuel"}>{user.use_fuel ? <BadgeCheck color="green" /> : <BadgeAlert color="#FFBF00" />}</TableCell>
                  <TableCell title={user.use_refrigerant ? "uses refrigerant" : "does not use refrigerant"}>{user.use_refrigerant ? <BadgeCheck color="green" /> : <BadgeAlert color="#FFBF00" />}</TableCell>
                  <TableCell title={user.use_ind_gas ? "uses industrial gas" : "does not use industrial gas"}>{user.use_ind_gas ? <BadgeCheck color="green" /> : <BadgeAlert color="#FFBF00" />}</TableCell>
                  <TableCell>
                    <Switch
                      checked={user.is_active}
                      color='success'
                      onChange={(e) => handleActivateDeactivate(user)}
                      disabled={isSubmitting}
                    />
                  </TableCell>
                  <TableCell>
                    {user.created_date ? format(new Date(user.created_date), "dd-MM-yyyy") : ""}
                  </TableCell>
                  <TableCell sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleEdit(user)}
                      sx={{
                        backgroundColor: '#16A34A',
                        color: 'white',
                        '&:hover': { backgroundColor: '#00bf46' },
                        p: '8px',
                      }}
                    >
                      <PenBoxIcon size={20} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleRemove(user.id)}
                      sx={{
                        backgroundColor: '#df3e3e',
                        color: 'white',
                        '&:hover': { backgroundColor: '#ff0000' },
                        p: '8px',
                      }}
                    >
                      <DeleteIcon size={20} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  colSpan={7}
                  count={totalEquipmentTypes}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                   labelDisplayedRows={({ from, to, count }) => 
                    `Page ${page + 1} of ${Math.ceil(count / rowsPerPage)} | ${from}-${to} of ${count}`
                  }
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>

        <Dialog
          open={isAddUpdateEquipmentTypeModalOpen}
          onClose={() => { setIsAddUpdateEquipmentTypeModalOpen(false); setSelectedEquipmentType(null) }}
          maxWidth="sm"
          fullWidth
        >
          <div className="flex justify-between items-start pt-2 px-2">
            <DialogTitle className="text-lg font-bold mt-1">
              {selectedEquipmentType ? "Edit Equipment Type" : "Add New Equipment Type"}
            </DialogTitle>
            <X
              className="w-6 h-6 cursor-pointer text-gray-500 hover:text-gray-700 transition"
              onClick={() => { setIsAddUpdateEquipmentTypeModalOpen(false); setSelectedEquipmentType(null) }}
            />
          </div>
          <DialogContent >
            <AddUpdateEquipmentTypeModal
              refetch={getAllEquipmentTypes}
              onClose={() => {
                setIsAddUpdateEquipmentTypeModalOpen(false);
                setSelectedEquipmentType(null);
              }}
              typeId={selectedEquipmentType?.id}
              initialData={selectedEquipmentType}
            />
          </DialogContent>
        </Dialog>

        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete the equipment type "{equipmentTypeToDelete?.type_name}"? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <div className="flex justify-end gap-5 items-center mt-5">
              <CustomButton
                className="bg-gray-500 hover:bg-gray-700 text-white"
                text="Cancel"
                disabled={isSubmitting}
                click={() => setDeleteDialogOpen(false)}
              />
              <CustomButton
                className="bg-red-600 hover:bg-red-700 text-white"
                click={confirmDelete}
                text='Delete'
                disabled={isSubmitting}
              />
            </div>
          </DialogActions>
        </Dialog>
      </PageHeader>
    </>
  )
}

export default EquipmentTypesMaster